import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { ShieldAlert, AlertTriangle, RefreshCw, Users, FileText, Download, Mail, Plus, Printer } from "lucide-react";
import {
  getBullyingMonitor, recomputeBullying,
  dismissBullyingWarning, getBullyingInvestigations, getBullyingInvestigation,
  updateBullyingInvestigation, generateInvestigationPacket, downloadInvestigationPacket,
  getInvestigationDue, listHhbLetters, downloadHhbLetter, markHhbLetterSent,
} from "../../lib/api";
import { triggerBlobDownload } from "../../lib/download";
import LetterWizard from "../bullying/LetterWizard";
import HhbPrintPanel from "../bullying/HhbPrintPanel";
import OpenInvestigationDialog from "../bullying/OpenInvestigationDialog";

const STATUS_OPTIONS = ["Open", "In Progress", "Closed - Substantiated", "Closed - Not Substantiated"];

function statusColor(s = "") {
  if (s === "Open") return "bg-red-100 text-red-800";
  if (s === "In Progress") return "bg-amber-100 text-amber-800";
  if (s.startsWith("Closed")) return "bg-gray-200 text-gray-700";
  return "bg-gray-100 text-gray-700";
}

export default function BullyingMonitorTab({ staffList = [], students = [], formerStudents = [] }) {
  const [monitor, setMonitor] = useState(null);
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState("");
  const [openInv, setOpenInv] = useState(null);
  const [showOpenDialog, setShowOpenDialog] = useState(false);

  const roster = [...(students || []), ...(formerStudents || [])];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, invs] = await Promise.all([
        getBullyingMonitor(), getBullyingInvestigations(),
      ]);
      setMonitor(m.data);
      setInvestigations(invs.data || []);
    } catch (e) {
      toast.error("Failed to load bullying monitor");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runRecompute = async () => {
    try {
      const { data } = await recomputeBullying();
      setMonitor(data);
      const invs = await getBullyingInvestigations();
      setInvestigations(invs.data || []);
      toast.success("Recalculated");
    } catch { toast.error("Failed to recalculate"); }
  };

  const confirmDismiss = async () => {
    if (!dismissReason.trim()) { toast.error("A reason is required"); return; }
    try {
      await dismissBullyingWarning(dismissTarget.pair_key, dismissReason.trim());
      toast.success("Warning dismissed");
      setDismissTarget(null); setDismissReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to dismiss"); }
  };

  if (loading) return <div className="h-40 bg-gray-100 rounded animate-pulse" />;

  const activeWarnings = (monitor?.warnings || []).filter((w) => !w.dismissed);

  return (
    <div className="space-y-6" data-testid="bullying-monitor-tab">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#00305E]" /> Bullying &amp; Harassment Monitor</h3>
          <p className="text-sm text-muted-foreground">
            Flags repeated victim–aggressor patterns in incident reports for human review. The system never determines that bullying occurred — that decision is yours.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={runRecompute} data-testid="bullying-recompute-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Recalculate</Button>
      </div>

      {/* Open an investigation manually (e.g. a complaint with no incident report yet) */}
      <div className="flex justify-end">
        <Button size="sm" className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => setShowOpenDialog(true)} data-testid="monitor-open-investigation-btn">
          <Plus className="w-4 h-4 mr-1.5" />Open Investigation
        </Button>
      </div>

      {/* Active window (read-only; edit in Settings) */}
      <Card data-testid="bullying-window-card">
        <CardContent className="py-3 flex items-start justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="font-medium text-[#00305E]" data-testid="bullying-window-plain">
              {monitor?.window_description || "Counting incidents in the current window."}
            </span>
            {monitor?.window_label && <span className="text-muted-foreground"> · {monitor.window_label}</span>}
          </div>
          <a href="/settings?tab=bullying-detection" className="text-sm text-blue-600 hover:underline" data-testid="bullying-window-settings-link">
            Change window &amp; thresholds in Settings →
          </a>
        </CardContent>
      </Card>

      {/* Warnings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Active Warnings ({activeWarnings.length})</CardTitle>
          <CardDescription>Pairs approaching the investigation threshold, plus single students targeted by multiple aggressors.</CardDescription>
        </CardHeader>
        <CardContent>
          {activeWarnings.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="bullying-no-warnings">No active warnings.</p>
          ) : (
            <div className="space-y-2">
              {activeWarnings.map((w) => (
                <div key={w.pair_key} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/60" data-testid={`bullying-warning-${w.pair_key}`}>
                  <div className="flex-1 min-w-0">
                    {w.kind === "multi_aggressor" ? (
                      <p className="text-sm"><Users className="w-4 h-4 inline mr-1 text-amber-600" /><span className="font-semibold">{w.victim_name}</span> has been targeted by <span className="font-semibold">{w.aggressor_count}</span> different students.</p>
                    ) : (
                      <p className="text-sm"><span className="font-semibold">{w.victim_name}</span> has been listed as the target of <span className="font-semibold">{w.aggressor_name}</span> in <span className="font-semibold">{w.count}</span> incident reports. One more incident will trigger an investigation.</p>
                    )}
                    <p className="text-xs text-amber-700 mt-1">{w.report_ids?.length || w.count} related incident report(s)</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => { setDismissTarget(w); setDismissReason(""); }} data-testid={`bullying-dismiss-${w.pair_key}`}>Dismiss</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Investigations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-600" /> Investigations ({investigations.length})</CardTitle>
          <CardDescription>Automatically opened when a pair reaches the investigation threshold. Admin-only.</CardDescription>
        </CardHeader>
        <CardContent>
          {investigations.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="bullying-no-investigations">No investigations on record.</p>
          ) : (
            <div className="space-y-2">
              {investigations.map((inv) => (
                <button key={inv.id} onClick={() => setOpenInv(inv.id)} data-testid={`bullying-investigation-${inv.id}`}
                  className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-lg border hover:border-[#00305E] transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-medium"><span className="text-red-700">{inv.victim_name}</span> targeted by <span className="text-orange-700">{inv.aggressor_name}</span></p>
                    <p className="text-xs text-muted-foreground">Opened {inv.date_opened} · {inv.linked_report_ids?.length || inv.pair_count} linked reports {inv.assigned_investigator_name ? `· ${inv.assigned_investigator_name}` : "· unassigned"}</p>
                  </div>
                  <Badge className={statusColor(inv.status)}>{inv.status}</Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dismiss dialog */}
      <AlertDialog open={!!dismissTarget} onOpenChange={(o) => { if (!o) { setDismissTarget(null); setDismissReason(""); } }}>
        <AlertDialogContent data-testid="bullying-dismiss-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Dismiss warning</AlertDialogTitle>
            <AlertDialogDescription>A reason is required and logged. Dismissing does not delete the underlying incident counts; the warning will reappear if another incident is added for this pair.</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} rows={3}
            placeholder="Reason (e.g. Reports were mis-tagged)" className="w-full text-sm border rounded px-2 py-1.5" data-testid="bullying-dismiss-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDismiss(); }} data-testid="bullying-dismiss-confirm">Dismiss</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {openInv && (
        <InvestigationDialog invId={openInv} staffList={staffList} onClose={() => setOpenInv(null)} onSaved={load} />
      )}

      <OpenInvestigationDialog
        open={showOpenDialog}
        onClose={() => setShowOpenDialog(false)}
        students={roster}
        onOpened={async (invId) => { await load(); if (invId) setOpenInv(invId); }}
      />
    </div>
  );
}

export function InvestigationDialog({ invId, staffList, onClose, onSaved }) {
  const [inv, setInv] = useState(null);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [pkVersion, setPkVersion] = useState("full");
  const [pkFocus, setPkFocus] = useState("");
  const [pkAttach, setPkAttach] = useState(true);
  const [pkNotes, setPkNotes] = useState(true);
  const [pkLetters, setPkLetters] = useState(true);
  const [pkLetterCopies, setPkLetterCopies] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [due, setDue] = useState(null);
  const [hhbLetters, setHhbLetters] = useState([]);
  const [wizardStage, setWizardStage] = useState(null);
  const [reprintStage, setReprintStage] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getBullyingInvestigation(invId);
      setInv(data);
      setDue(data.due || null);
      setDraft({
        status: data.status,
        assigned_investigator_id: data.assigned_investigator_id || "",
        victim_iep_504: data.victim_iep_504 || "",
        aggressor_iep_504: data.aggressor_iep_504 || "",
        closing_summary: data.closing_summary || "",
        actions_taken: data.actions_taken || "",
      });
      setPkFocus(data.victim_id || "");
      try { const lr = await listHhbLetters(invId); setHhbLetters(lr.data || []); } catch { /* ignore */ }
    } catch { toast.error("Failed to load investigation"); }
  }, [invId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      const inv2 = staffList.find((s) => s.id === draft.assigned_investigator_id);
      payload.assigned_investigator_name = inv2 ? inv2.name : null;
      if (note.trim()) payload.note = note.trim();
      await updateBullyingInvestigation(invId, payload);
      toast.success("Investigation updated");
      setNote("");
      await load();
      onSaved && onSaved();
    } catch { toast.error("Failed to update"); }
    setSaving(false);
  };

  const generatePacket = async () => {
    if (pkVersion === "redacted" && !pkFocus) { toast.error("Pick the student this packet is about"); return; }
    setGenerating(true);
    try {
      const payload = {
        version: pkVersion,
        redact_focus_student_id: pkVersion === "redacted" ? pkFocus : null,
        include_attachments: pkAttach,
        include_notes: pkNotes,
        include_letters: pkLetters,
        include_letter_copies: pkLetterCopies,
      };
      const res = await generateInvestigationPacket(invId, payload);
      const disp = res.headers["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      triggerBlobDownload(res.data, m ? m[1] : `investigation_packet_${pkVersion}.pdf`);
      toast.success("Investigation packet generated");
      setPacketOpen(false);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate packet");
    }
    setGenerating(false);
  };

  const redownload = async (p) => {
    try {
      const res = await downloadInvestigationPacket(invId, p.id);
      triggerBlobDownload(res.data, p.filename || "investigation_packet.pdf");
    } catch (e) { toast.error(e.response?.data?.detail || "Packet no longer available"); }
  };

  const markLetterSent = async (lid, method) => {
    try {
      await markHhbLetterSent(invId, lid, { method });
      const lr = await listHhbLetters(invId);
      setHhbLetters(lr.data || []);
      toast.success("Marked sent");
    } catch { toast.error("Failed to mark sent"); }
  };

  const initLetters = hhbLetters.filter((l) => l.stage === "initiation" && !l.superseded);
  const outLetters = hhbLetters.filter((l) => l.stage === "outcome" && !l.superseded);
  const invClosed = (inv?.status || "").startsWith("Closed");
  const allLetters = [...initLetters, ...outLetters];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="investigation-dialog">
        <DialogHeader>
          <DialogTitle>Investigation</DialogTitle>
          <DialogDescription>{inv ? <>{inv.victim_name} (target) · {inv.aggressor_name} (aggressor) · opened {inv.date_opened}</> : "Loading…"}</DialogDescription>
        </DialogHeader>
        {inv && (
          <div className="space-y-4">
            {/* Deadline + letters */}
            {due && due.due_date && (
              <div className={`rounded-lg p-2.5 text-sm flex items-center gap-2 ${due.overdue ? "bg-red-50 text-red-800 border border-red-200" : due.days_left <= 2 ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-50 text-slate-700 border"}`} data-testid="investigation-deadline">
                <AlertTriangle className="w-4 h-4" />
                {due.closed ? <>Closed · investigation was due {due.due_date}</>
                  : due.overdue ? <>Overdue — was due {due.due_date}</>
                  : <>Investigation due {due.due_date} · {due.days_left} school day(s) left</>}
              </div>
            )}
            <div className="rounded-lg border p-3 space-y-2" data-testid="hhb-letters-panel">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-sm font-semibold flex items-center gap-1.5"><Mail className="w-4 h-4 text-[#00305E]" /> HHB Notification Letters</span>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant={initLetters.length ? "outline" : "default"} className={initLetters.length ? "" : "bg-[#00305E]"} onClick={() => setWizardStage("initiation")} data-testid="generate-initiation-letters">
                    {initLetters.length ? "Re-run Initiation" : "Generate Initiation Letters"}
                  </Button>
                  {initLetters.length > 0 && (
                    <Button size="sm" variant="outline" onClick={() => setReprintStage("initiation")} data-testid="reprint-initiation-letters">
                      <Printer className="w-3.5 h-3.5 mr-1" />Print Initiation
                    </Button>
                  )}
                  {invClosed && (
                    <Button size="sm" variant={outLetters.length ? "outline" : "default"} className={outLetters.length ? "" : "bg-[#00305E]"} onClick={() => setWizardStage("outcome")} data-testid="generate-outcome-letters">
                      {outLetters.length ? "Re-run Outcome" : "Generate Outcome Letters"}
                    </Button>
                  )}
                  {outLetters.length > 0 && (
                    <Button size="sm" variant="outline" onClick={() => setReprintStage("outcome")} data-testid="reprint-outcome-letters">
                      <Printer className="w-3.5 h-3.5 mr-1" />Print Outcome
                    </Button>
                  )}
                </div>
              </div>
              {initLetters.length === 0 && (
                <p className="text-xs text-amber-700" data-testid="init-not-sent-banner">Initiation letters not yet generated.</p>
              )}
              {invClosed && outLetters.length === 0 && (
                <p className="text-xs text-amber-700" data-testid="outcome-not-sent-banner">Outcome letters not yet generated.</p>
              )}
              {allLetters.length > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="hhb-sent-hint">
                  Downloading a letter only prints it — it does not mark it sent. Use "Mark sent" once each letter has actually been mailed, emailed, or hand delivered.
                </p>
              )}
              {allLetters.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 text-xs border-t pt-1.5" data-testid={`hhb-letter-${l.id}`}>
                  <span className="min-w-0 truncate">{l.stage === "outcome" ? "[Outcome] " : "[Initiation] "}{l.label} → {l.recipient_name}
                    {l.sent ? <span className="text-emerald-700 ml-1">· sent {l.sent.date} ({l.sent.method})</span> : <span className="text-amber-700 ml-1">· not sent</span>}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {l.sent ? (
                      <Select onValueChange={(m) => markLetterSent(l.id, m)}>
                        <SelectTrigger className="h-6 w-[108px] text-[11px]" data-testid={`hhb-letter-sent-${l.id}`}><SelectValue placeholder="Change…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mail">Mail</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Hand delivered">Hand delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Select onValueChange={(m) => markLetterSent(l.id, m)}>
                        <SelectTrigger className="h-6 w-[108px] text-[11px]" data-testid={`hhb-letter-sent-${l.id}`}><SelectValue placeholder="Mark sent…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mail">Mail</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Hand delivered">Hand delivered</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button variant="ghost" size="sm" className="h-6 px-2" onClick={async () => {
                      try { const r = await downloadHhbLetter(invId, l.id, false); triggerBlobDownload(r.data, `${l.label}.pdf`); } catch { toast.error("Download failed"); }
                    }} data-testid={`hhb-letter-dl-${l.id}`}><Download className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="investigation-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Assigned investigator</Label>
                <Select value={draft.assigned_investigator_id || "none"} onValueChange={(v) => setDraft((p) => ({ ...p, assigned_investigator_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="investigation-investigator"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{inv.victim_name} — IEP / 504?</Label>
                <Input value={draft.victim_iep_504} onChange={(e) => setDraft((p) => ({ ...p, victim_iep_504: e.target.value }))} placeholder="e.g. IEP, 504, None" data-testid="investigation-victim-iep" />
              </div>
              <div className="space-y-1.5">
                <Label>{inv.aggressor_name} — IEP / 504?</Label>
                <Input value={draft.aggressor_iep_504} onChange={(e) => setDraft((p) => ({ ...p, aggressor_iep_504: e.target.value }))} placeholder="e.g. IEP, 504, None" data-testid="investigation-aggressor-iep" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Linked incident reports ({(inv.linked_reports || []).length})</Label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {(inv.linked_reports || []).map((r) => (
                  <div key={r.id} className="text-xs p-2 border rounded bg-gray-50" data-testid={`investigation-report-${r.id}`}>
                    <span className="font-medium">{r.date}</span>{r.incident_type ? ` · ${r.incident_type}` : ""} — {r.description?.slice(0, 160)}
                  </div>
                ))}
                {(inv.linked_reports || []).length === 0 && <p className="text-xs text-muted-foreground">No linked reports available.</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Notes / timeline</Label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {(inv.notes || []).map((n, i) => (
                  <div key={i} className="text-xs p-2 border rounded bg-white">
                    <div className="font-medium text-gray-700">{n.author_name} · {n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                    <div className="text-gray-600 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))}
                {(inv.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
              </div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note (append-only)…"
                className="w-full text-sm border rounded px-2 py-1.5" data-testid="investigation-note" />
            </div>

            <div className="space-y-1.5">
              <Label>Closing summary</Label>
              <textarea value={draft.closing_summary} onChange={(e) => setDraft((p) => ({ ...p, closing_summary: e.target.value }))} rows={3}
                placeholder="Summary when closing the investigation…" className="w-full text-sm border rounded px-2 py-1.5" data-testid="investigation-closing-summary" />
            </div>

            <div className="space-y-1.5">
              <Label>Actions taken / measures put in place</Label>
              <textarea value={draft.actions_taken} onChange={(e) => setDraft((p) => ({ ...p, actions_taken: e.target.value }))} rows={3}
                placeholder="e.g. Safety plan, schedule changes, supports added…" className="w-full text-sm border rounded px-2 py-1.5" data-testid="investigation-actions-taken" />
            </div>

            <div className="space-y-1.5 border-t pt-4">
              <Label className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#00305E]" /> Generated Packets ({(inv.generated_packets || []).length})</Label>
              {(inv.generated_packets || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No packets generated yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(inv.generated_packets || []).slice().reverse().map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 text-xs p-2 border rounded bg-gray-50" data-testid={`investigation-packet-${p.id}`}>
                      <div className="min-w-0">
                        <span className="font-medium capitalize">{p.version}</span>
                        {p.version === "redacted" && p.redact_focus_student_name ? ` · about ${p.redact_focus_student_name}` : ""}
                        {" · "}{p.generated_by_name}{" · "}{p.generated_at ? new Date(p.generated_at).toLocaleString() : ""}
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => redownload(p)} data-testid={`investigation-packet-download-${p.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
          <Button variant="outline" onClick={() => setPacketOpen(true)} disabled={saving || !inv} data-testid="generate-packet-btn">
            <FileText className="w-4 h-4 mr-1.5" />Generate Investigation Packet
          </Button>
          <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={save} disabled={saving} data-testid="investigation-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>

        {wizardStage && inv && (
          <LetterWizard
            invId={invId}
            inv={inv}
            stage={wizardStage}
            onClose={() => setWizardStage(null)}
            onDone={() => { load(); onSaved && onSaved(); }}
          />
        )}

        {reprintStage && inv && (
          <Dialog open onOpenChange={(o) => { if (!o) setReprintStage(null); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hhb-reprint-dialog">
              <DialogHeader>
                <DialogTitle>Print {reprintStage === "outcome" ? "Outcome" : "Initiation"} Letters</DialogTitle>
                <DialogDescription>Reprint finalized letters and mark them sent. This does not create new letter versions.</DialogDescription>
              </DialogHeader>
              <HhbPrintPanel invId={invId} stage={reprintStage} />
              <DialogFooter>
                <Button variant="outline" onClick={() => { setReprintStage(null); load(); }} data-testid="hhb-reprint-close">Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {inv && (
          <Dialog open={packetOpen} onOpenChange={setPacketOpen}>
            <DialogContent className="max-w-md" data-testid="packet-options-dialog">
              <DialogHeader>
                <DialogTitle>Generate Investigation Packet</DialogTitle>
                <DialogDescription>Choose how this PDF should be prepared before generating.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Version</Label>
                  <Select value={pkVersion} onValueChange={setPkVersion}>
                    <SelectTrigger data-testid="packet-version"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full (internal file) — real names</SelectItem>
                      <SelectItem value="redacted">Redacted for sharing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {pkVersion === "redacted" && (
                  <div className="space-y-1.5">
                    <Label>This packet is about…</Label>
                    <Select value={pkFocus} onValueChange={setPkFocus}>
                      <SelectTrigger data-testid="packet-focus"><SelectValue placeholder="Pick a student" /></SelectTrigger>
                      <SelectContent>
                        {inv.victim_id && <SelectItem value={inv.victim_id}>{inv.victim_name} (alleged victim)</SelectItem>}
                        {inv.aggressor_id && <SelectItem value={inv.aggressor_id}>{inv.aggressor_name} (alleged aggressor)</SelectItem>}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">The selected student is shown by initials; everyone else becomes "Student A", "Student B", … everywhere — including inside report text.</p>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="packet-attachments">
                  <input type="checkbox" checked={pkAttach} onChange={(e) => setPkAttach(e.target.checked)} /> Include attachments
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="packet-notes">
                  <input type="checkbox" checked={pkNotes} onChange={(e) => setPkNotes(e.target.checked)} /> Include investigator notes
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="packet-letters">
                  <input type="checkbox" checked={pkLetters} onChange={(e) => setPkLetters(e.target.checked)} /> Include notification letters summary
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="packet-letter-copies">
                  <input type="checkbox" checked={pkLetterCopies} onChange={(e) => setPkLetterCopies(e.target.checked)} /> Include full letter copies
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPacketOpen(false)} disabled={generating}>Cancel</Button>
                <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={generatePacket} disabled={generating} data-testid="packet-generate-confirm">
                  {generating ? "Generating…" : "Generate PDF"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
